class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    /* A11y 2026-09-18 (C2): `inert` is the deterministic half of the closed
       state — it takes the whole subtree out of the tab order and out of
       the accessibility tree the instant the drawer closes, with no
       dependence on the CSS visibility transition resolving (it does not
       resolve at all in a backgrounded tab). The CSS still handles the
       visuals. Mirrored off `.active` via an observer so every path that
       opens or closes the drawer is covered, including the section-refresh
       paths that add the class directly instead of calling open(). */
    this.syncInert();
    new MutationObserver(() => this.syncInert()).observe(this, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  syncInert() {
    if (this.classList.contains('active')) this.removeAttribute('inert');
    else this.setAttribute('inert', '');
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    /* A11y 2026-09-18 (C1): Dawn only traps focus on transitionend. The
       BoochBod drawer swaps its inner markup (refreshCartDrawer, the ATC
       intercept) so the transitioning element is often replaced before the
       event fires and focus never enters the dialog (WCAG 2.4.3 / 2.1.2).
       Trap on transitionend when it comes, otherwise on a timer just past
       the 0.2s slide, and never twice for one open(). */
    let trapped = false;
    const doTrap = () => {
      if (trapped) return;
      trapped = true;
      this.trapCartFocus();
    };
    this.addEventListener('transitionend', doTrap, { once: true });
    setTimeout(doTrap, 320);

    document.body.classList.add('overflow-hidden');
  }

  /* Bind (or re-bind after a content swap) the focus trap on the open drawer.
     Keeps focus where it is when it is already inside the drawer, otherwise
     moves it to the dialog container. Safe to call repeatedly.

     The retry matters: `.drawer` transitions `visibility`, and an element
     inside a visibility:hidden subtree cannot take focus at all — .focus()
     is a silent no-op. If the trap runs before that transition resolves
     (and in a backgrounded tab it never resolves, which is how this was
     found on the draft, 2026-09-18) focus would stay on the trigger and
     the dialog would be open behind the keyboard. So: verify focus
     actually landed, fall back to the close button, then retry briefly. */
  trapCartFocus(attempt) {
    if (!this.classList.contains('active')) return;
    // The observer is async; an inert subtree cannot take focus.
    this.removeAttribute('inert');
    const containerToTrapFocusOn = this.classList.contains('is-empty')
      ? this.querySelector('.drawer__inner-empty') || document.getElementById('CartDrawer')
      : document.getElementById('CartDrawer');
    if (!containerToTrapFocusOn) return;

    const current = document.activeElement;
    if (current && this.contains(current) && current !== document.body) {
      trapFocus(containerToTrapFocusOn, current);
      return;
    }

    const target = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
    if (!target) return;
    trapFocus(containerToTrapFocusOn, target);
    if (this.contains(document.activeElement)) return;

    const close = this.querySelector('.drawer__close');
    if (close) {
      close.focus();
      if (this.contains(document.activeElement)) return;
    }

    const next = (attempt || 0) + 1;
    if (next <= 12) setTimeout(() => this.trapCartFocus(next), 120);
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
